import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import isPropValid from '@emotion/is-prop-valid';
import { stylingHelpers } from '../../designTokens';

import { childrenPropType, componentsPropType } from '../lib/shared-prop-types';

const { textByte, textMega, textGiga, textTera, flexCenter, disableVisually } = stylingHelpers;

const BORDER_WIDTH = '1px';

const baseStyles = ({ theme }) => css`
  font-family: ${theme.fontStack.bold.name};
  font-weight: ${theme.fontStack.bold.weight};
  color: ${theme.colors.white};
  background-color: ${theme.colors.red};
  border: 1px solid ${theme.colors.red};
  border-radius: ${theme.borderRadius.peta};
  cursor: pointer;
  padding: 6px ${theme.spacings.mega} ${theme.spacings.giga};
  position: relative;

  ${textMega({ theme })}
  ${flexCenter()}  

  &:focus,
  &:active {
    outline: none;
  }

  &:disabled,
  &[disabled] {
    ${disableVisually()};
    background: ${theme.colors.n700};
    border-color: ${theme.colors.n700};
    color: ${theme.colors.white};
  }
`;

const primaryStyles = ({ theme, variant = 'secondary' }) =>
  variant === 'primary' &&
  css`
    background-color: ${theme.colors.p500};
    border-color: ${theme.colors.p500};
    color: ${theme.colors.white};
  `;

const secondaryStyles = ({ theme, variant = 'secondary' }) =>
  variant === 'secondary' &&
  css`
    background-color: ${theme.colors.y500};
    background: linear-gradient(180deg, ${theme.colors.y500} 0%, ${theme.colors.o300} 50%);
    border-color: ${theme.colors.o300};
    color: ${theme.colors.black};

    &:disabled,
    &[disabled] {
      ${disableVisually()};
      background: ${theme.colors.y500};
      border-color: ${theme.colors.o300};
      color: ${theme.colors.black};
      opacity: 0.6;
    }
  `;

const tertiaryStyles = ({ theme, variant = 'secondary' }) =>
  variant === 'tertiary' &&
  css`
    background-color: ${theme.colors.white};
    border-color: ${theme.colors.red};
    color: ${theme.colors.red};
  `;

const alternateStyles = ({ theme, variant = 'secondary' }) =>
  variant === 'alternate' &&
  css`
    background-color: ${theme.colors.white};
    border-color: ${theme.colors.white};
    color: ${theme.colors.red};
  `;

const contrastStyles = ({ theme, variant = 'secondary' }) =>
  variant === 'contrast' &&
  css`
    background-color: ${theme.colors.red};
    border-color: ${theme.colors.red};
    color: ${theme.colors.white};
  `;

const sizeStyles = ({ theme, hasIcon, size = 'tera' }) => {
  const sizeMap = {
    byte: {
      padding: `${theme.spacings.bit} ${theme.spacings.byte}`,
      borderRadius: theme.borderRadius.peta,
      type: textByte({ theme }),
    },
    kilo: {
      padding: `${theme.spacings.byte} calc(${theme.spacings.giga} - ${BORDER_WIDTH})`,
      borderRadius: theme.borderRadius.peta,
      type: textMega({ theme }),
    },
    giga: {
      padding: `${theme.spacings.bit} calc(${theme.spacings.giga} - ${BORDER_WIDTH})`,
      borderRadius: theme.borderRadius.peta,
      type: textGiga({ theme }),
    },
    peta: {
      padding: `${theme.spacings.mega}`,
      borderRadius: theme.borderRadius.peta,
      type: textTera({ theme }),
    },
    tera: {
      padding: `${theme.spacings[hasIcon ? 'byte' : 'kilo']} ${theme.spacings.giga}`,
      paddingLeft: theme.spacings[hasIcon ? 'byte' : 'giga'],
      borderRadius: theme.borderRadius.peta,
      type: textTera({ theme }),
      minWidth: '230px',
    },
  };

  const { type, ...rest } = sizeMap[size] || 'byte';

  return css`
    ${type};
    ${rest};
  `;
};

const stretchStyles = ({ stretch }) =>
  stretch &&
  css`
    width: 100%;
  `;

const BaseButton = styled('button', {
  shouldForwardProp: (prop) => isPropValid(prop) && prop !== 'size',
})(
  baseStyles,
  primaryStyles,
  secondaryStyles,
  tertiaryStyles,
  alternateStyles,
  contrastStyles,
  sizeStyles,
  stretchStyles,
);

const iconContainerBaseStyles = ({ theme }) =>
  css`
    color: ${theme.colors.black};
    margin-right: ${theme.spacings.mega};
  `;

const IconContainer = styled('div')(iconContainerBaseStyles);

const Button = ({ children, icon, onSelect, ...props }) => (
  <BaseButton {...props} onClick={onSelect}>
    {icon && <IconContainer>{icon}</IconContainer>}
    {children}
  </BaseButton>
);

Button.propTypes = {
  children: childrenPropType,
  icon: componentsPropType,
  onSelect: PropTypes.func,
};

export default Button;
